import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Loader2, MessageSquareQuote } from "lucide-react";
import { api, errorText } from "@/lib/api";
import type { Spot, SpotReview } from "@/lib/spots";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";

export const Stars = ({ value, onPick, size = "h-4 w-4", testId }: { value: number; onPick?: (n: number) => void; size?: string; testId?: string }) => (
  <span className="inline-flex gap-0.5" dir="ltr" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((n) => {
      const icon = <Star className={`${size} ${n <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />;
      return onPick ? <button key={n} type="button" data-testid={`${testId}-${n}`} onClick={() => onPick(n)} className="p-0.5 active:scale-90 transition-transform">{icon}</button> : <span key={n}>{icon}</span>;
    })}
  </span>
);

const MyReview = ({ spotId, onDone }: { spotId: string; onDone: () => void }) => {
  const { t } = usePrefs();
  const [mine, setMine] = useState<SpotReview | null | undefined>(undefined);
  const [rating, setRating] = useState(0);
  const [tip, setTip] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    api.get(`/me/spot-reviews/${spotId}`).then((r) => { setMine(r.data); if (r.data) { setRating(r.data.rating); setTip(r.data.tip); } }).catch(() => setMine(null));
  }, [spotId]);

  const send = async () => {
    if (!rating) return toast.error(t("rev.pickStars"));
    setBusy(true);
    try {
      const { data } = await api.post(`/public-spots/${spotId}/reviews`, { rating, tip });
      setMine(data); setEditing(false); toast.success(t("rev.sent")); onDone();
    } catch (e) { toast.error(errorText(e, t)); }
    setBusy(false);
  };

  if (mine === undefined) return null;
  if (mine && !editing) return (
    <div data-testid="my-review" className="rounded-2xl border bg-card p-4 space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-bold">{t("rev.mine")}</span>
        <span data-testid="my-review-status" className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${mine.status === "approved" ? "bg-emerald-100 text-emerald-700" : mine.status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{t(`rev.status.${mine.status}`)}</span>
      </div>
      <Stars value={mine.rating} />
      {mine.tip && <p className="text-sm">{mine.tip}</p>}
      {mine.status === "rejected" && mine.reject_reason && <p className="text-xs text-rose-600">{t("rev.reason")}: {mine.reject_reason}</p>}
      <button data-testid="my-review-edit" onClick={() => setEditing(true)} className="text-sm font-bold text-primary">{t("rev.edit")}</button>
    </div>
  );
  return (
    <div data-testid="review-form" className="rounded-2xl border bg-card p-4 space-y-3">
      <p className="font-bold">{t("rev.ask")}</p>
      <Stars value={rating} onPick={setRating} size="h-7 w-7" testId="review-stars" />
      <textarea data-testid="review-tip" value={tip} maxLength={300} onChange={(e) => setTip(e.target.value)} rows={2} placeholder={t("rev.tipPh")} className="w-full rounded-xl border bg-background p-3 text-sm" />
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{t("rev.moderated")}</span>
        <button data-testid="review-submit" disabled={busy} onClick={send} className="h-10 px-5 rounded-full bg-primary text-primary-foreground font-bold text-sm inline-flex items-center gap-1.5 disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{t("rev.send")}</button>
      </div>
    </div>
  );
};

export const SpotReviews = ({ spot }: { spot: Spot }) => {
  const { t } = usePrefs();
  const { user } = useAuth();
  const [rows, setRows] = useState<SpotReview[]>([]);
  const load = () => api.get(`/public-spots/${spot.spot_id}/reviews`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [spot.spot_id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section data-testid="spot-reviews" className="space-y-4 pt-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 className="font-display text-xl font-black">{t("rev.title")}</h2>
        {!!spot.rating_count && <span data-testid="spot-rating-summary" className="inline-flex items-center gap-1.5 text-sm font-bold"><Stars value={Math.round(spot.rating_avg || 0)} />{spot.rating_avg} · {t("rev.count").replace("{n}", String(spot.rating_count))}</span>}
      </div>
      {user && user.role === "customer" ? <MyReview spotId={spot.spot_id} onDone={load} /> : !user && (
        <Link to="/login" data-testid="review-login" className="inline-flex h-10 px-5 items-center rounded-full border font-bold text-sm">{t("rev.login")}</Link>
      )}
      {rows.length === 0 ? <p data-testid="reviews-empty" className="text-sm text-muted-foreground">{t("rev.empty")}</p> : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.review_id} data-testid={`review-${r.review_id}`} className="rounded-2xl bg-accent/60 p-4 space-y-1">
              <div className="flex items-center justify-between gap-2"><span className="font-bold text-sm">{r.author}</span><Stars value={r.rating} /></div>
              {r.tip && <p className="text-sm flex gap-2"><MessageSquareQuote className="h-4 w-4 shrink-0 text-muted-foreground mt-0.5" />{r.tip}</p>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
